import React, { useMemo } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCalculatorSimple,
  faInfoCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { capitalize } from "lodash-es";
import { useFieldArray } from "react-hook-form";

import { Card } from "src/base-components/Card";
import { FeatureType } from "src/clients/features-control";
import { Accordion } from "src/design-system/Accordion";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import {
  ColumnNameDisplay,
  FeatureDescriptionEditor,
  FeatureFieldInput,
} from "src/featureCatalogue/Queries/Create/FeatureFormFields";
import {
  ColumnType,
  columnDatatypesFromColumns,
  getPreviewColumnNames,
} from "src/featureCatalogue/Queries/Create/helpers";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { inferDataTypes } from "src/featureCatalogue/Queries/SQLResonsePreviewTable";
import { UseFeatureQueryPreview } from "src/featureCatalogue/queries";
import { TYPE_ICONS } from "src/utils/constants";

export const FeatureSection: React.FC<{
  readonly: boolean;
  featureQueryPreview: UseFeatureQueryPreview;
  columnTypes: Record<string, ColumnType>;
  isLoading: boolean;
}> = ({
  readonly,
  featureQueryPreview,
  columnTypes: assignedColumnTypes,
  isLoading,
}) => {
  const { fields } = useFieldArray<QueryFormValues, "columns.features">({
    name: "columns.features",
  });

  const preview = featureQueryPreview.previewData;

  const columnNames = getPreviewColumnNames(preview);

  // Infer column datatypes for the type icons — from returned rows when
  // present, otherwise from the backend column schema.
  const columnTypes = useMemo(() => {
    if (preview?.data?.length) return inferDataTypes(preview.data);
    return columnDatatypesFromColumns(preview?.columns);
  }, [preview]);

  const columnsOptions = columnNames
    .filter(
      (column) =>
        assignedColumnTypes[column] !== "entity" &&
        assignedColumnTypes[column] !== "grouping_key",
    )
    .map((column) => ({
      key: column,
      value: capitalize(column.replace(/_/g, " ")),
      icon: TYPE_ICONS[columnTypes[column] ?? "any"],
      type: columnTypes[column],
    }));

  // Hide features whose column is not in the current preview ("orphans").
  // Their data stays in form state so it returns automatically if the column
  // is added back to SQL — but they're not rendered or sent to the backend
  // (orphans are filtered out at submit time in ManageFeatureQueryPage).
  // Original indices are preserved so each FeatureCard's form path stays
  // correct (`columns.features.${originalIndex}.…`).
  // When the preview hasn't been run yet (e.g. opening a saved FQ for edit
  // before clicking Run Query), show all features — there are no orphans to
  // hide because we have nothing to compare against.
  const hasPreviewColumns = columnNames.length > 0;
  const visibleFields = fields
    .map((field, originalIndex) => ({ field, originalIndex }))
    .filter(({ field }) => {
      if (!hasPreviewColumns) return true;
      return columnNames.includes(field.column_name);
    });

  return (
    <Accordion.Item
      dataLoc="feature-section"
      isLoading={isLoading}
      label="Define Features"
      value="define-features"
    >
      <div className="flex flex-col gap-y-4">
        {visibleFields.map(({ field, originalIndex }) => (
          <FeatureCard
            key={field.id}
            columnsOptions={columnsOptions}
            index={originalIndex}
            readonly={readonly}
          />
        ))}
      </div>
    </Accordion.Item>
  );
};

const FeatureCard: React.FC<{
  index: number;
  readonly: boolean;
  columnsOptions: Array<{
    key: string;
    value: string;
    icon: IconProp;
    type: FeatureType | undefined;
  }>;
}> = ({ index, readonly, columnsOptions }) => {
  return (
    <Card className="w-full space-y-3" dataLoc={`feature-card-${index}`}>
      <Card.Header>
        <div className="flex items-center justify-center gap-x-1">
          <Card.Title title={`Feature ${index + 1}`} />
          <Tooltip body="Each Feature maps a Query Column to an output field and can include a Default value.">
            <Icon icon={faInfoCircle} size="2xs" />
          </Tooltip>
        </div>
      </Card.Header>
      <Card.Content>
        <div className="flex flex-col gap-y-4">
          <div className="grid grid-cols-2 gap-4">
            <ColumnNameDisplay columnsOptions={columnsOptions} index={index} />
            <FeatureFieldInput
              dataLoc={`feature-name-${index}`}
              disabled={readonly}
              fieldName={`columns.features.${index}.name`}
              label="Name the Feature"
              placeholder="Add Feature name"
              prefixIcon={{
                icon: faCalculatorSimple,
              }}
              tooltip="The display name for this feature"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FeatureFieldInput
              dataLoc={`feature-key-${index}`}
              disabled={readonly}
              fieldName={`columns.features.${index}.feature_key`}
              label="Feature Key"
              placeholder="Add Feature key"
              trimOnChange
            />
            <FeatureFieldInput
              dataLoc={`feature-default-${index}`}
              disabled={readonly}
              fieldName={`columns.features.${index}.feature_default`}
              label="Default Value"
              placeholder="None"
              tooltip="The value used if no Events are found for the Entity within the selected time window."
            />
          </div>
          <FeatureDescriptionEditor disabled={readonly} index={index} />
        </div>
      </Card.Content>
    </Card>
  );
};
